/** CLAÈ Aesthetics — Homepage CMS.
 * Left: per-section editor (text, visibility, colours). Right: live
 * iframe preview at /?preview=1 that receives postMessage updates on
 * every keystroke — nothing is published until "Publish" is clicked. */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Save, Send, RotateCcw, Undo2, ExternalLink, Eye, EyeOff, ChevronDown } from "lucide-react";

const SECTIONS = [
  { k: "hero", label: "Hero" },
  { k: "edit", label: "The CLAÈ Edit" },
  { k: "approach", label: "The CLAÈ Approach" },
  { k: "model", label: "Model Days" },
  { k: "consult", label: "Consultation" },
  { k: "instagram", label: "Instagram" },
  { k: "footer", label: "Footer" },
];

export default function AdminHomepage() {
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [openSec, setOpenSec] = useState("hero");
  const iframeRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/homepage/draft").then((r) => { setDraft(r.data); setDirty(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Push the current draft into the iframe on every change so preview
  // updates in real time without a network round-trip.
  useEffect(() => {
    if (!draft) return;
    const w = iframeRef.current?.contentWindow;
    w && w.postMessage({ type: "clae-preview", content: draft }, "*");
  }, [draft]);

  const set = (path, value) => {
    setDirty(true);
    setDraft((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i]; const nk = parts[i + 1];
        if (/^\d+$/.test(nk) && !Array.isArray(cur[key])) cur[key] = [];
        else if (cur[key] == null) cur[key] = {};
        cur = cur[key];
      }
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const saveDraft = async () => {
    await api.put("/admin/homepage/draft", draft);
    setDirty(false);
    toast.success("Draft saved");
  };
  const publish = async () => {
    if (dirty) await api.put("/admin/homepage/draft", draft);
    await api.post("/admin/homepage/publish");
    setDirty(false);
    toast.success("Published — the homepage is now live");
  };
  const discard = async () => {
    if (!window.confirm("Discard current draft and revert to the published version?")) return;
    await api.post("/admin/homepage/discard");
    load();
    toast.success("Draft reverted");
  };
  const reset = async () => {
    if (!window.confirm("Reset EVERYTHING to CLAÈ defaults? This clears both draft and live homepage.")) return;
    await api.post("/admin/homepage/reset");
    load();
    // Force iframe reload so it picks up the reset public content
    if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
    toast.success("Homepage reset to defaults");
  };

  if (!draft) return <div className="p-6 text-stone-500">Loading homepage…</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 -mx-6" data-testid="admin-homepage">
      {/* ═════════ EDITOR ═════════ */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-serif text-3xl">Homepage</h2>
            <p className="text-xs text-stone-500 mt-1">
              Edit every headline, body, button and section visibility. Changes are
              draft-only until you press Publish. {dirty && <span className="ml-2 mono-label text-amber-700">UNSAVED CHANGES</span>}
            </p>
          </div>
        </div>

        {/* Action bar (sticky) */}
        <div className="sticky top-0 z-10 bg-[#FAF9F6] py-2 border-b hairline flex flex-wrap gap-2 mb-4">
          <button onClick={saveDraft} data-testid="hp-save-draft"
                  className="mono-label border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white flex items-center gap-1">
            <Save size={12} /> SAVE DRAFT
          </button>
          <button onClick={publish} data-testid="hp-publish"
                  className="mono-label bg-stone-900 text-white px-3 py-2 hover:bg-[#B99A6B] flex items-center gap-1"
                  style={{ color: "#fff" }}>
            <Send size={12} /> PUBLISH
          </button>
          <button onClick={discard} data-testid="hp-discard"
                  className="mono-label border hairline px-3 py-2 hover:bg-red-50 hover:text-red-700 flex items-center gap-1">
            <Undo2 size={12} /> DISCARD
          </button>
          <button onClick={reset} data-testid="hp-reset"
                  className="mono-label border hairline px-3 py-2 hover:bg-red-50 hover:text-red-700 flex items-center gap-1">
            <RotateCcw size={12} /> RESET
          </button>
          <a href="/?preview=1" target="_blank" rel="noreferrer" data-testid="hp-preview-external"
             className="ml-auto mono-label border hairline px-3 py-2 flex items-center gap-1">
            <ExternalLink size={12} /> OPEN PREVIEW
          </a>
        </div>

        {/* Sections */}
        <div className="space-y-3" data-testid="hp-sections">
          {SECTIONS.map((sec) => (
            <Section key={sec.k} sec={sec}
                     open={openSec === sec.k} onToggle={() => setOpenSec(openSec === sec.k ? null : sec.k)}
                     draft={draft} set={set} />
          ))}

          {/* Colours */}
          <Colours draft={draft} set={set} />

          {/* Spacing */}
          <div className="border hairline bg-white p-4">
            <p className="mono-label mb-2">Spacing</p>
            <div className="flex gap-2">
              {["compact", "standard", "spacious"].map((s) => (
                <button key={s} onClick={() => set("spacing", s)}
                        data-testid={`hp-spacing-${s}`}
                        className={`mono-label text-xs border px-3 py-2 ${draft.spacing === s ? "bg-stone-900 text-white border-stone-900" : "hairline hover:border-stone-900"}`}
                        style={draft.spacing === s ? { color: "#fff" } : {}}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═════════ LIVE PREVIEW ═════════ */}
      <div className="px-6" data-testid="hp-preview-panel">
        <div className="sticky top-4">
          <div className="border hairline bg-white overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: 640 }}>
            <iframe ref={iframeRef} src="/?preview=1" title="Homepage preview"
                    className="w-full h-full" data-testid="hp-preview-iframe" />
          </div>
          <p className="text-xs text-stone-500 mt-2">Preview updates in real time. Publish to make changes live for clients.</p>
        </div>
      </div>
    </div>
  );
}

function Section({ sec, open, onToggle, draft, set }) {
  const k = sec.k;
  const s = draft[k] || {};
  return (
    <div className={`border hairline bg-white ${open ? "shadow-sm" : ""}`} data-testid={`hp-sec-${k}`}>
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <span className={`inline-block w-1.5 h-6 ${s.show ? "bg-emerald-400" : "bg-stone-300"}`} />
          <p className="font-serif text-lg">{sec.label}</p>
          {!s.show && <span className="mono-label text-[10px] text-stone-500">HIDDEN</span>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={(e) => { e.stopPropagation(); set(`${k}.show`, !s.show); }}
                  data-testid={`hp-toggle-${k}`}
                  className="mono-label text-xs border hairline px-2 py-1 hover:bg-alt flex items-center gap-1">
            {s.show ? <><Eye size={12} /> SHOWN</> : <><EyeOff size={12} /> HIDDEN</>}
          </button>
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t hairline space-y-3">
          {k === "hero" && <HeroFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "edit" && <EditFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "approach" && <ApproachFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "model" && <SplitFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "consult" && <SplitFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "instagram" && <IgFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
          {k === "footer" && <FooterFields s={s} set={(p, v) => set(`${k}.${p}`, v)} />}
        </div>
      )}
    </div>
  );
}

const F = ({ label, value, onChange, hint, textarea, testid }) => (
  <label className="block">
    <span className="mono-label text-[10px] text-stone-500">{label}</span>
    {textarea ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full border hairline p-2 text-sm mt-1" />
    ) : (
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full border hairline p-2 text-sm mt-1" />
    )}
    {hint && <p className="text-[10px] text-stone-400 mt-1">{hint}</p>}
  </label>
);

function HeroFields({ s, set }) {
  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    const token = localStorage.getItem("clae_token");
    try {
      const resp = await fetch(`${API}/uploads/image`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!resp.ok) throw new Error("upload failed");
      const data = await resp.json();
      set("art_url", data.url);
      toast.success("Hero image uploaded — remember to Publish");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };
  return (<>
    <F label="Eyebrow" value={s.eyebrow} onChange={(v) => set("eyebrow", v)} testid="hp-hero-eyebrow" />
    <F label="Heading (use \\n for line break)" textarea value={s.heading} onChange={(v) => set("heading", v)} testid="hp-hero-heading" />
    <F label="Supporting text" value={s.sub} onChange={(v) => set("sub", v)} testid="hp-hero-sub" />
    <div className="grid grid-cols-2 gap-3">
      <F label="Button text" value={s.cta_text} onChange={(v) => set("cta_text", v)} testid="hp-hero-cta" />
      <F label="Button link" value={s.cta_href} onChange={(v) => set("cta_href", v)} testid="hp-hero-cta-href" />
    </div>
    <div className="border-t hairline pt-3 mt-2 space-y-2">
      <p className="mono-label text-[10px] text-stone-500">Hero image</p>
      {s.art_url && (
        <div className="flex items-start gap-3">
          <img src={s.art_url} alt="Hero preview" data-testid="hp-hero-art-preview"
               className="w-24 h-24 object-cover border hairline" />
          <button type="button" onClick={() => set("art_url", "")} data-testid="hp-hero-art-remove"
                  className="mono-label text-[10px] text-stone-500 hover:text-stone-900 underline">
            Remove image
          </button>
        </div>
      )}
      <input type="file" accept="image/*" onChange={onUpload} disabled={uploading}
             data-testid="hp-hero-art-file"
             className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:border file:border-stone-900 file:bg-white file:text-stone-900 file:mono-label file:cursor-pointer file:hover:bg-stone-900 file:hover:text-white" />
      <F label="…or paste an image URL (leave blank for the champagne gradient)"
         value={s.art_url} onChange={(v) => set("art_url", v)} testid="hp-hero-art" />
    </div>
  </>);
}

function EditFields({ s, set }) {
  return (<>
    <F label="Eyebrow" value={s.eyebrow} onChange={(v) => set("eyebrow", v)} />
    <F label="Heading" textarea value={s.heading} onChange={(v) => set("heading", v)} />
    <F label="Body" textarea value={s.body} onChange={(v) => set("body", v)} />
    <div className="grid grid-cols-2 gap-3">
      <F label="View-all text" value={s.view_all_text} onChange={(v) => set("view_all_text", v)} />
      <F label="View-all link" value={s.view_all_href} onChange={(v) => set("view_all_href", v)} />
    </div>
    <div className="border-t hairline pt-3 mt-2">
      <p className="mono-label mb-2">Categories</p>
      {(s.cats || []).map((c, i) => (
        <div key={i} className="border hairline p-3 mb-2 bg-stone-50">
          <div className="flex items-center justify-between mb-2">
            <span className="mono-label text-[10px]">CATEGORY {c.n}</span>
            <button onClick={() => set(`cats.${i}.show`, !c.show)}
                    className="mono-label text-[10px] border hairline px-2 py-0.5">
              {c.show === false ? "HIDDEN" : "SHOWN"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Number" value={c.n} onChange={(v) => set(`cats.${i}.n`, v)} />
            <F label="Title" value={c.title} onChange={(v) => set(`cats.${i}.title`, v)} />
          </div>
          <F label="Body" textarea value={c.body} onChange={(v) => set(`cats.${i}.body`, v)} />
          <F label="Link" value={c.href} onChange={(v) => set(`cats.${i}.href`, v)} />
        </div>
      ))}
    </div>
  </>);
}

function ApproachFields({ s, set }) {
  return (<>
    <F label="Eyebrow" value={s.eyebrow} onChange={(v) => set("eyebrow", v)} />
    <F label="Heading" textarea value={s.heading} onChange={(v) => set("heading", v)} />
    <F label="Body" textarea value={s.body} onChange={(v) => set("body", v)} />
    <div className="grid grid-cols-2 gap-3">
      <F label="Button text" value={s.cta_text} onChange={(v) => set("cta_text", v)} />
      <F label="Button link" value={s.cta_href} onChange={(v) => set("cta_href", v)} />
    </div>
    <F label="Art image URL (optional)" value={s.art_url} onChange={(v) => set("art_url", v)} />
    <div>
      <span className="mono-label text-[10px] text-stone-500">Layout</span>
      <div className="flex gap-2 mt-1">
        {["image-left", "image-right"].map((l) => (
          <button key={l} onClick={() => set("layout", l)}
                  className={`mono-label text-[10px] border px-2 py-1 ${s.layout === l ? "bg-stone-900 text-white border-stone-900" : "hairline"}`}
                  style={s.layout === l ? { color: "#fff" } : {}}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  </>);
}

function SplitFields({ s, set }) {
  return (<>
    <F label="Eyebrow" value={s.eyebrow} onChange={(v) => set("eyebrow", v)} />
    <F label="Heading" textarea value={s.heading} onChange={(v) => set("heading", v)} />
    <F label="Body" textarea value={s.body} onChange={(v) => set("body", v)} />
    <div className="grid grid-cols-2 gap-3">
      <F label="Button text" value={s.cta_text} onChange={(v) => set("cta_text", v)} />
      <F label="Button link" value={s.cta_href} onChange={(v) => set("cta_href", v)} />
    </div>
  </>);
}

function IgFields({ s, set }) {
  return (<>
    <F label="Eyebrow" value={s.eyebrow} onChange={(v) => set("eyebrow", v)} />
    <div className="grid grid-cols-2 gap-3">
      <F label="Handle" value={s.handle} onChange={(v) => set("handle", v)} />
      <F label="Handle URL" value={s.handle_url} onChange={(v) => set("handle_url", v)} />
    </div>
    <p className="mono-label mb-1">Tile image URLs (5 tiles)</p>
    {(s.tiles || ["","","","",""]).slice(0, 5).map((t, i) => (
      <F key={i} label={`Tile ${i + 1}`} value={t} onChange={(v) => set(`tiles.${i}`, v)} />
    ))}
  </>);
}

function FooterFields({ s, set }) {
  return (<>
    <p className="mono-label">Address (one line per row)</p>
    {(s.address_lines || []).map((line, i) => (
      <F key={i} label={`Line ${i + 1}`} value={line} onChange={(v) => set(`address_lines.${i}`, v)} />
    ))}
    <F label="Brand line" value={s.brand_line} onChange={(v) => set("brand_line", v)} />
    <p className="mono-label mt-3">Navigation</p>
    {(s.nav || []).map((n, i) => (
      <div key={i} className="grid grid-cols-2 gap-2">
        <F label={`Label ${i + 1}`} value={n.label} onChange={(v) => set(`nav.${i}.label`, v)} />
        <F label={`Link ${i + 1}`} value={n.href} onChange={(v) => set(`nav.${i}.href`, v)} />
      </div>
    ))}
    <p className="mono-label mt-3">Social</p>
    {(s.social || []).map((n, i) => (
      <div key={i} className="grid grid-cols-2 gap-2">
        <F label={`Label ${i + 1}`} value={n.label} onChange={(v) => set(`social.${i}.label`, v)} />
        <F label={`Link ${i + 1}`} value={n.href} onChange={(v) => set(`social.${i}.href`, v)} />
      </div>
    ))}
  </>);
}

function Colours({ draft, set }) {
  const c = draft.colours || {};
  const rows = [
    ["ivory", "Main background · Warm ivory"],
    ["cream", "Secondary background · Soft cream"],
    ["beige", "Neutral · Light beige"],
    ["taupe", "Muted · Taupe"],
    ["champagne", "Accent · Subtle champagne"],
    ["ink", "Text · Charcoal"],
    ["ink_soft", "Muted text · Warm grey"],
  ];
  return (
    <div className="border hairline bg-white p-4">
      <p className="mono-label mb-3">Colours</p>
      <div className="space-y-2">
        {rows.map(([k, label]) => (
          <div key={k} className="flex items-center gap-3">
            <input type="color" value={c[k] || "#000000"}
                   onChange={(e) => set(`colours.${k}`, e.target.value)}
                   data-testid={`hp-color-${k}`}
                   className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer" />
            <div className="flex-1">
              <p className="text-xs text-stone-700">{label}</p>
              <p className="mono-label text-[10px] text-stone-400">{c[k]}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
